/* ==========================================================================
   AISSIST — Three.js scene + GSAP motion + Lenis smooth scroll
   --------------------------------------------------------------------------
   One entry point, loaded by every page. It wires only what it finds.

   SCROLL FEEL
     Lenis owns the scroll position and GSAP's ticker drives it, so wheel,
     trackpad and keyboard all land on one inertial curve instead of the
     browser's stepped scrolling. ScrollTrigger reads from Lenis, which is
     what makes scroll-linked motion feel continuous rather than snapped.

   THE SCENE — dots and discrete objects, no grid or mesh lines:
     farDots  4,800 static specks                      -> distance
     dots     9,600 interactive field, cursor pushes   -> the surface you touch
     rings        3 tori on different tilts            -> gyroscope frame
     shards      18 octahedra on individual orbits     -> volume
     knot     1,500 nodes, scattered <-> ordered       -> the data
     pulses     220 red signals riding the strand      -> work moving
     streams     60 red streaks travelling outward     -> output leaving
     core         1 red icosahedron                    -> the engine
     halo         1 soft red bloom                     -> heat

   THE CHOREOGRAPHY
     Headings reveal through a translate mask (compositor-only, unlike a
     blur) and fire a `flash` into the 3D field on the same beat, so type
     arrives with the scene rather than on top of it.
   ========================================================================== */

/* style.css is linked from each page's <head>, not imported here. Imported
   from JS, the dev server injects it only after this module has run, so every
   page load first painted with no styles at all: a white page with the
   loader's "i" drawn as a giant black circle and arch. */

import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  BufferGeometry,
  BufferAttribute,
  Float32BufferAttribute,
  Points,
  PointsMaterial,
  LineLoop,
  LineSegments,
  LineBasicMaterial,
  WireframeGeometry,
  IcosahedronGeometry,
  OctahedronGeometry,
  Group,
  Color,
  CanvasTexture,
  AdditiveBlending,
  DynamicDrawUsage,
} from 'three';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger, SplitText);

/* ==========================================================================
   SOCIAL LINKS — fill these in
   --------------------------------------------------------------------------
   Paste the full profile address between the quotes. This is the only place
   they need to be set: every Instagram / LinkedIn / X icon on the site (the
   footer of both pages and the contact page) reads from here.

   While an address is empty its icon still shows, but it is not clickable,
   so the site never sends a visitor to a dead link.
   ========================================================================== */
const SOCIAL_LINKS = {
  instagram: '', // e.g. 'https://www.instagram.com/aissist'
  linkedin: '', // e.g. 'https://www.linkedin.com/company/aissist'
  twitter: '', // e.g. 'https://x.com/aissist'
};

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Apple's interfaces lean on one shape of curve: quick out of the gate, long
   gentle settle, never a bounce. These are that curve at three lengths. */
const EASE = {
  out: 'power3.out',
  settle: 'expo.out',
  inOut: 'power2.inOut',
};

const COLORS = {
  farDots: '#2b3444',
  dotNear: '#aab6cf',
  dotFar: '#48546d',
  ring: '#4d5872',
  shard: '#6b7998',
  core: '#ff4a3d',
  nodeCool: '#7f90b4',
  nodeWarm: '#ffffff',
  signalHot: '#ff3b30',
  signalCore: '#ffb0a0',
  stream: '#ff5f4d',
  halo: '#e01b2d',
};

const CAMERA_REST_Z = 21;

const KNOT_P = 3;
const KNOT_Q = 5;
const KNOT_SCALE = 1.95;
const TUBE_SPREAD = 0.95;

const NODE_COUNT = 1500;
const FAR_DOT_COUNT = 4800; // static parallax depth
const DOT_COUNT = 9600; // the interactive field the cursor pushes around
const PULSE_COUNT = 220;
const STREAM_COUNT = 60;
const SHARD_COUNT = 18;

/* Cursor repulsion. RADIUS is in world units on the z=0 plane; PUSH is how
   far a dot at the very centre of the cursor is thrown. EASE is the per-frame
   approach rate — low enough that dots drift back rather than snapping, which
   is what makes it feel like a field rather than a collision. */
/* How much bigger the centrepiece (the spiral and everything orbiting it) is
   at the bottom of the page than at the top. On top of this, the scroll dolly
   also moves the camera closer, so by the end you are inside it. */
const SCROLL_GROWTH = 0.85;

const REPEL_RADIUS = 9;
const REPEL_PUSH = 5;
const REPEL_EASE = 0.075;

/* Refresh-rate policy.

   Rendering happens every frame the display offers. Simulation — rewriting
   thousands of particle positions — does not need to: above 60Hz the extra
   sub-steps are invisible, so on a 120Hz screen simulating every frame would
   double the CPU cost for nothing. These tiers cap the simulation rate and,
   if frames are still being missed, shed work in order of least visual cost.

   Tier 2 is the default; the loop steps down on sustained slow frames and
   back up when there is headroom, with hysteresis so it cannot oscillate. */
const QUALITY_TIERS = [
  { dpr: 1.25, simHz: 30, farDots: false, dotRatio: 0.45 },
  { dpr: 1.5, simHz: 45, farDots: true, dotRatio: 0.7 },
  { dpr: 2, simHz: 60, farDots: true, dotRatio: 1 },
];

const SLOW_FRAME_MS = 22; // below ~45fps
const FAST_FRAME_MS = 13; // comfortably above 60fps
const FRAMES_BEFORE_DOWNGRADE = 45;
const FRAMES_BEFORE_UPGRADE = 240;

/* ==========================================================================
   Smooth scroll
   ========================================================================== */

function initSmoothScroll() {
  if (REDUCED_MOTION) return null;

  const lenis = new Lenis({
    // An exponential settle: most of the distance early, then a long glide.
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - 2 ** (-10 * t)),
    smoothWheel: true,
    wheelMultiplier: 0.9,
    touchMultiplier: 1.5,
  });

  // ScrollTrigger must read Lenis's position, not the browser's.
  lenis.on('scroll', ScrollTrigger.update);

  // One clock for both libraries — two RAF loops would beat against each other.
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // In-page anchors have to go through Lenis or they jump.
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      // No offset here: Lenis already honours the section's scroll-margin-top,
      // and adding one as well parked every section twice the header's height
      // below it. The margin is also what a native #hash arrival uses, so
      // both paths now land in the same place.
      lenis.scrollTo(target, { duration: 1.4 });
    });
  });

  return lenis;
}

/* ==========================================================================
   Loading screen — the logo's "i" glyph, twisting down and up
   ========================================================================== */

/* The intro timeline runs 3.22s (nav links start at 2.45s and take 0.77s
   with their stagger), after waiting up to 0.9s for the document. The hard
   deadline was 3.2s — shorter than the intro itself — so it fired on every
   first visit and yanked the cover off mid-fade. It now clears the longest
   possible intro with room to spare; the CSS safety net in style.css sits
   later still. */
const INTRO_WAIT_CAP_MS = 900;
const INTRO_TIMELINE_MS = 3220;
const LOADER_DEADLINE_MS = INTRO_WAIT_CAP_MS + INTRO_TIMELINE_MS + 700; // 4.82s

function markSeen() {
  try {
    sessionStorage.setItem('aissist:seen', '1');
  } catch {
    /* private mode — the intro simply plays again next time */
  }
}

/** Park the loader off-screen without destroying it; transitions reuse it. */
function parkLoader(loader) {
  loader.style.animation = 'none'; // release the CSS safety net
  gsap.set(loader, { opacity: 0, visibility: 'hidden', pointerEvents: 'none' });
}

/**
 * The glyph hovers: it floats up and down on one loop while turning over on
 * another, with the shadow tightening as it rises. Three independent loops
 * rather than one sequence, so nothing ever lands on a seam — at any moment
 * you are looking at a thing suspended in the air, not a clip replaying.
 *
 * Returns everything started so the caller can stop it.
 */
function startHover(loader) {
  const glyph = loader.querySelector('.loader__glyph');
  const shadow = loader.querySelector('.loader__shadow');

  const turn = gsap.to(glyph, {
    rotateX: 360,
    duration: 1.6,
    ease: 'none',
    repeat: -1,
  });

  const float = gsap.to(glyph, {
    y: -14,
    duration: 0.95,
    ease: 'sine.inOut',
    yoyo: true,
    repeat: -1,
  });

  // The shadow reads the height: small and faint when the glyph is up.
  const cast = gsap.to(shadow, {
    scaleX: 0.55,
    opacity: 0.15,
    duration: 0.95,
    ease: 'sine.inOut',
    yoyo: true,
    repeat: -1,
  });

  return [turn, float, cast];
}

/** Stop every loop started by startHover. */
function stopHover(loops) {
  loops.forEach((loop) => loop.kill());
}

/**
 * First load of a session: the logo is the subject. It appears centred and
 * large, then flies up into its place in the header.
 *
 * This is a FLIP move on the real `.nav__logo` element — measure where it
 * lives, transform it to the middle of the screen, then animate back to
 * identity. Animating the actual element rather than a centred copy means
 * there is no hand-off flicker when it lands.
 */
function playLogoIntro(loader, finish) {
  const nav = document.querySelector('.nav');
  const logo = document.querySelector('.nav__logo');
  const label = loader.querySelector('.loader__label');

  if (!logo) {
    return gsap.to(loader, { opacity: 0, duration: 0.4, onComplete: finish });
  }

  nav?.classList.add('nav--intro');

  const rect = logo.getBoundingClientRect();
  const introHeight = Math.min(window.innerHeight * 0.13, 104);
  const scale = introHeight / rect.height;
  const dx = window.innerWidth / 2 - (rect.left + rect.width / 2);
  const dy = window.innerHeight / 2 - (rect.top + rect.height / 2);

  gsap.set(logo, { x: dx, y: dy, scale, transformOrigin: 'center center' });

  return (
    gsap
      .timeline({
        onComplete: () => {
          // Drop every inline transform so the logo behaves normally afterwards.
          gsap.set(logo, { clearProps: 'all' });
          nav?.classList.remove('nav--intro');
          finish();
        },
      })
      .fromTo(
        logo,
        { opacity: 0, scale: scale * 0.94 },
        { opacity: 1, scale, duration: 0.95, ease: EASE.settle },
        0,
      )
      .fromTo(
        label,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.9, ease: EASE.settle },
        0.3,
      )
      .to(label, { opacity: 0, y: -10, duration: 0.4, ease: 'power2.in' }, 1.45)
      // the flight: one long move, no bounce at either end
      .to(logo, { x: 0, y: 0, scale: 1, duration: 1.25, ease: 'expo.inOut' }, 1.65)
      .to(loader, { opacity: 0, duration: 0.85, ease: EASE.inOut }, 2.0)
      // fromTo with an explicit end, never .from(). A .from() takes the current
      // value as its destination — and .nav--intro has already hidden these with
      // CSS, so it animated opacity 0 -> 0 and left an inline `opacity: 0` behind
      // that outlived the class. The header nav vanished on every first visit.
      // clearProps hands them back to the stylesheet once they're shown.
      .fromTo(
        ['.nav__links', '.nav .cta'],
        { opacity: 0, y: -8 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: EASE.settle,
          stagger: 0.07,
          clearProps: 'opacity,transform',
        },
        2.45,
      )
  );
}

/**
 * Arriving on any page after the first: nothing covers the page at all.
 *
 * An opaque cover here was the bug behind "blank screens between pages" —
 * the content underneath was fully rendered and ready, but a dark panel with
 * a small glyph sat on top of it for the better part of a second, which
 * reads as the page having failed to load. The glyph belongs on the way
 * *out*, where it fills the wait for the next document; on the way in the
 * page should simply be there.
 */
function playArrival(loader, finish) {
  // No lift of <main> either. It used to start at opacity 0.55 — but only
  // once this script ran, after the page had already painted at full
  // opacity, so every arrival flickered bright → dim → bright. The browser's
  // own fade and the hero reveals are the arrival.
  finish();
}

function runLoader(lenis) {
  const loader = document.getElementById('loader');
  if (!loader) return Promise.resolve();

  if (REDUCED_MOTION) {
    parkLoader(loader);
    markSeen();
    return Promise.resolve();
  }

  const isIntro = document.documentElement.classList.contains('boot-intro');

  lenis?.stop();

  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(deadline);
      parkLoader(loader);
      lenis?.start();
      markSeen();
      resolve();
    };

    let intro = null;

    /* Hard deadline. The timelines below only advance while the frame loop
       runs, and it does not in a background tab. Without this the cover
       would stay up and — far worse — Lenis would stay stopped, leaving the
       page unscrollable once the visitor came back. setTimeout still fires
       when requestAnimationFrame does not.

       It jumps the intro to its end rather than just calling finish(): the
       intro's own onComplete is what puts the logo back in the header and
       shows the nav links. Skipping it cleared the cover but left the logo
       stuck, huge, in the middle of the page. */
    const deadline = setTimeout(() => {
      if (intro) intro.progress(1);
      finish();
    }, LOADER_DEADLINE_MS);

    if (!isIntro) {
      playArrival(loader, finish);
      return;
    }

    // Wait for the document before presenting the logo, but never stall on it.
    const loaded = new Promise((r) => {
      if (document.readyState === 'complete') r();
      else window.addEventListener('load', r, { once: true });
    });
    const cap = new Promise((r) => setTimeout(r, INTRO_WAIT_CAP_MS));

    Promise.race([loaded, cap]).then(() => {
      intro = playLogoIntro(loader, finish);
    });
  });
}

/* ==========================================================================
   Page-to-page transitions
   ========================================================================== */

/**
 * Clicking an internal link brings the "i" up, hovering, for about a second,
 * then navigates. The incoming page is not covered at all (see playArrival).
 */
function initPageTransitions(lenis, field) {
  const loader = document.getElementById('loader');
  if (!loader || REDUCED_MOTION) return;

  // One page, however its address is spelled: /contact.html, /contact and
  // /contact/ are the same page, and so are /index.html, /index and /.
  const norm = (path) =>
    path
      .replace(/\/index(\.html)?\/?$/, '/')
      .replace(/\.html$/, '')
      .replace(/(.)\/$/, '$1');
  let leaving = false;
  let exit = null; // the running exit: its timeline, loops and fallback

  /* Back and forward restore a page from the back/forward cache exactly as it
     was left — which, after a click, is mid-exit: cover up, glyph hovering,
     Lenis stopped, the scene blown outward. Without this the visitor comes
     back to a page they cannot see or scroll. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    if (exit) {
      clearTimeout(exit.fallback);
      exit.timeline.kill();
      stopHover(exit.hover);
      exit = null;
    }

    loader.classList.remove('loader--exit');
    parkLoader(loader);
    gsap.set(loader.querySelector('.loader__stage'), { clearProps: 'all' });
    gsap.set(loader.querySelector('.loader__glyph'), { clearProps: 'all' });
    gsap.set(loader.querySelector('.loader__shadow'), { clearProps: 'all' });

    if (field)
      gsap.to(field, { burst: 0, signal: 0.42, duration: 0.8, ease: EASE.settle });
    lenis?.start();
    leaving = false;
  });

  // Any link to another page of this site, including ones with a #section
  // (the contact page links to index.html#services).
  //
  // Decided from where the link goes, never from how its href is written.
  // This used to match only hrefs containing ".html" — and Netlify's Pretty
  // URLs rewrites ./contact.html to /contact and ./index.html to / when it
  // deploys, so on the live site no link matched and the hovering "i" never
  // appeared. mailto:, other sites and same-page #anchors fall through below.
  document.querySelectorAll('a[href]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download') || leaving) return;

      const url = new URL(link.href);
      if (url.origin !== window.location.origin) return;
      if (norm(url.pathname) === norm(window.location.pathname)) return;

      event.preventDefault();
      leaving = true;

      // The next document boots without the logo intro.
      markSeen();

      lenis?.stop();

      document.documentElement.classList.remove('boot-intro');
      document.documentElement.classList.add('boot-mini');

      loader.classList.add('loader--exit');
      const hover = startHover(loader);
      gsap.set(loader, { visibility: 'visible', pointerEvents: 'auto' });
      gsap.set(loader.querySelector('.loader__stage'), { scale: 0.9 });

      const href = link.getAttribute('href');
      let navigated = false;
      const go = () => {
        if (navigated) return;
        navigated = true;
        clearTimeout(fallback);
        stopHover(hover);
        window.location.href = href;
      };

      /* Same reasoning as the loader deadline: if the exit animation cannot
         advance, the click must still take the visitor somewhere rather than
         appearing to do nothing. */
      const fallback = setTimeout(go, 1800);

      const timeline = gsap
        .timeline({
          onComplete: go,
        })
        .to(loader, { opacity: 1, duration: 0.28, ease: 'power2.out' }, 0)
        .to(
          loader.querySelector('.loader__stage'),
          { scale: 1, duration: 0.5, ease: EASE.settle },
          0,
        )
        // hold long enough to read a full hover cycle, then go
        .to({}, { duration: 0.85 }, 0.28);

      exit = { timeline, hover, fallback };

      // The scene pushes outward on the way out. Only `burst`: raising `signal`
      // here also swelled the red bloom at the centre into a large glowing disc
      // while the cover was still fading in.
      if (field) {
        gsap.to(field, { burst: 1, duration: 0.7, ease: 'power2.in' });
      }
    });
  });
}

/* ==========================================================================
   The scene
   ========================================================================== */

function createGlowTexture() {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.2, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.18)');
  g.addColorStop(1, 'rgba(255,255,255,0)');

  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);

  return new CanvasTexture(canvas);
}

function knotPoint(t, breathe, out) {
  const radial = Math.cos(KNOT_Q * t) + 2 + breathe;
  out[0] = radial * Math.cos(KNOT_P * t);
  out[1] = radial * Math.sin(KNOT_P * t);
  out[2] = -Math.sin(KNOT_Q * t);
}

/** Sum of uniforms approximates a Gaussian: dense core, sparse halo. */
function gauss() {
  return (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;
}

function circleGeometry(radius, segments = 96) {
  const pts = [];
  for (let i = 0; i < segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    pts.push(Math.cos(a) * radius, Math.sin(a) * radius, 0);
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(pts, 3));
  return geo;
}

/** Spherical shell of points as a filled Float32Array. */
function shellPoints(count, minR, maxR, flatten = 1) {
  const arr = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    const r = minR + Math.random() * (maxR - minR);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * flatten;
    arr[i * 3 + 2] = r * Math.cos(phi);
  }
  return arr;
}

function initScene(canvas) {
  const scene = new Scene();

  const camera = new PerspectiveCamera(
    58,
    window.innerWidth / window.innerHeight,
    0.1,
    500,
  );
  camera.position.set(0, 0, CAMERA_REST_Z);

  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  renderer.setClearColor(0x000000, 0);

  let quality = QUALITY_TIERS.length - 1;

  // Never above 2, and never above what the current tier allows.
  const pixelRatio = () => Math.min(window.devicePixelRatio, QUALITY_TIERS[quality].dpr);

  function applySize() {
    const { innerWidth: w, innerHeight: h } = window;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(pixelRatio());
    renderer.setSize(w, h, false);
  }

  applySize();

  const glow = createGlowTexture();

  /* ----------------------------------------------------------- far dots */

  const farGeo = new BufferGeometry();
  farGeo.setAttribute(
    'position',
    new BufferAttribute(shellPoints(FAR_DOT_COUNT, 46, 150), 3),
  );

  const farDots = new Points(
    farGeo,
    new PointsMaterial({
      size: 0.44,
      map: glow,
      color: new Color(COLORS.farDots),
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      blending: AdditiveBlending,
      sizeAttenuation: true,
    }),
  );
  scene.add(farDots);

  /* ------------------------------------------------- the interactive field */

  /* These sit in their own group outside `swarm`, deliberately. The cursor
     repulsion is computed on the z=0 plane in world space; if the field
     inherited the swarm's rotation the maths would have to be undone every
     frame and the push would drift away from the pointer. */
  const dotBase = new Float32Array(DOT_COUNT * 3);
  const dotPos = new Float32Array(DOT_COUNT * 3);
  const dotOffset = new Float32Array(DOT_COUNT * 3); // current displacement
  const dotColor = new Float32Array(DOT_COUNT * 3);
  const dotPhase = new Float32Array(DOT_COUNT);

  // Each dot's random offset inside its cell, and its depth, chosen once.
  // Re-rolling them on every resize made the whole field teleport — which on
  // a phone happens constantly, because the address bar collapsing while you
  // scroll fires a resize.
  const dotJitter = new Float32Array(DOT_COUNT * 3);
  for (let i = 0; i < DOT_COUNT; i += 1) {
    dotJitter[i * 3] = (Math.random() - 0.5) * 0.9;
    dotJitter[i * 3 + 1] = (Math.random() - 0.5) * 0.9;
    dotJitter[i * 3 + 2] = (Math.random() - 0.5) * 16;
    dotPhase[i] = Math.random() * Math.PI * 2;
  }

  const dotNear = new Color(COLORS.dotNear);
  const dotFar = new Color(COLORS.dotFar);
  const dotMix = new Color();

  /**
   * Lay the dots out as a jittered grid across the whole visible plane, so
   * the background is evenly filled edge to edge rather than clustered in
   * the middle. A plain random scatter leaves visible clumps and bald
   * patches; one dot per cell with a random offset inside it reads as an
   * even field while still looking unplanned.
   *
   * Re-run on resize, because the visible extent depends on the aspect. It is
   * deterministic, so a re-run only rescales the field; nothing jumps.
   */
  function layoutDots() {
    // Sized for the camera at rest, not wherever the scroll dolly has it right
    // now. The dolly only ever moves the camera closer, which shows *less* of
    // the plane — so a field that fills the view at rest fills it at every
    // scroll position. Sizing from the live z meant a resize while scrolled
    // built a field too small for the top of the page.
    const halfH = Math.tan((camera.fov * Math.PI) / 360) * CAMERA_REST_Z * 1.22;
    const halfW = halfH * camera.aspect * 1.12;

    const aspect = halfW / halfH;
    const cols = Math.max(2, Math.round(Math.sqrt(DOT_COUNT * aspect)));
    const rows = Math.max(2, Math.ceil(DOT_COUNT / cols));

    const cellW = (halfW * 2) / cols;
    const cellH = (halfH * 2) / rows;

    for (let i = 0; i < DOT_COUNT; i += 1) {
      const i3 = i * 3;
      const col = i % cols;
      const row = Math.floor(i / cols);

      const x = -halfW + (col + 0.5 + dotJitter[i3]) * cellW;
      const y = -halfH + (row + 0.5 + dotJitter[i3 + 1]) * cellH;

      dotBase[i3] = x;
      dotBase[i3 + 1] = y;
      dotBase[i3 + 2] = dotJitter[i3 + 2];

      // Dots further from the middle sit back into the dark, so the field
      // never competes with the type in the centre of the screen.
      const falloff = Math.min(1, Math.hypot(x / halfW, y / halfH));
      dotMix.copy(dotNear).lerp(dotFar, falloff * 0.85);
      dotColor[i3] = dotMix.r;
      dotColor[i3 + 1] = dotMix.g;
      dotColor[i3 + 2] = dotMix.b;
    }
  }

  layoutDots();

  const dotGeo = new BufferGeometry();
  const dotAttr = new BufferAttribute(dotPos, 3);
  dotAttr.setUsage(DynamicDrawUsage);
  dotGeo.setAttribute('position', dotAttr);
  dotGeo.setAttribute('color', new BufferAttribute(dotColor, 3));

  const dots = new Points(
    dotGeo,
    new PointsMaterial({
      size: 0.165,
      map: glow,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      blending: AdditiveBlending,
      sizeAttenuation: true,
    }),
  );
  scene.add(dots);

  /* -------------------------------------------------------------- rings */

  const rings = new Group();
  [
    { radius: 9.6, tilt: [1.1, 0.2, 0], rate: 0.06 },
    { radius: 12.2, tilt: [0.3, 1.2, 0.4], rate: -0.042 },
    { radius: 14.6, tilt: [-0.8, 0.5, 1.0], rate: 0.028 },
  ].forEach((spec) => {
    const ring = new LineLoop(
      circleGeometry(spec.radius),
      new LineBasicMaterial({
        color: new Color(COLORS.ring),
        transparent: true,
        opacity: 0.08,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    );
    ring.rotation.set(...spec.tilt);
    ring.userData = { rate: spec.rate, baseTilt: [...spec.tilt] };
    rings.add(ring);
  });

  /* ------------------------------------------------------------- shards */

  const shardGeo = new WireframeGeometry(new OctahedronGeometry(0.38, 0));
  const shards = new Group();

  for (let i = 0; i < SHARD_COUNT; i += 1) {
    const shard = new LineSegments(
      shardGeo,
      new LineBasicMaterial({
        color: new Color(COLORS.shard),
        transparent: true,
        opacity: 0.12,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    );

    shard.userData = {
      orbitRadius: 15 + Math.random() * 9,
      orbitSpeed: (0.05 + Math.random() * 0.12) * (Math.random() < 0.5 ? -1 : 1),
      orbitPhase: Math.random() * Math.PI * 2,
      orbitTilt: (Math.random() - 0.5) * 1.4,
      spin: [Math.random() * 0.4, Math.random() * 0.4, Math.random() * 0.4],
      scale: 0.5 + Math.random() * 0.55,
      bob: Math.random() * Math.PI * 2,
    };

    shard.scale.setScalar(shard.userData.scale);
    shards.add(shard);
  }

  /* -------------------------------------------------------------- nodes */

  const nodePos = new Float32Array(NODE_COUNT * 3);
  const nodeCol = new Float32Array(NODE_COUNT * 3);

  const scatter = new Float32Array(NODE_COUNT * 3);
  const tBase = new Float32Array(NODE_COUNT);
  const offsets = new Float32Array(NODE_COUNT * 3);
  const phases = new Float32Array(NODE_COUNT);

  const cool = new Color(COLORS.nodeCool);
  const warm = new Color(COLORS.nodeWarm);
  const mix = new Color();

  for (let i = 0; i < NODE_COUNT; i += 1) {
    const i3 = i * 3;

    tBase[i] = (i / NODE_COUNT) * Math.PI * 2 + Math.random() * 0.012;

    offsets[i3] = gauss() * TUBE_SPREAD;
    offsets[i3 + 1] = gauss() * TUBE_SPREAD;
    offsets[i3 + 2] = gauss() * TUBE_SPREAD;
    phases[i] = Math.random() * Math.PI * 2;

    const r = 9 + Math.random() * 13;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    scatter[i3] = r * Math.sin(phi) * Math.cos(theta);
    scatter[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    scatter[i3 + 2] = r * Math.cos(phi) * 0.7;

    const d = Math.hypot(offsets[i3], offsets[i3 + 1], offsets[i3 + 2]);
    mix.copy(cool).lerp(warm, Math.max(0, 1 - d / TUBE_SPREAD) ** 1.6);
    nodeCol[i3] = mix.r;
    nodeCol[i3 + 1] = mix.g;
    nodeCol[i3 + 2] = mix.b;
  }

  const nodeGeo = new BufferGeometry();
  const nodeAttr = new BufferAttribute(nodePos, 3);
  nodeAttr.setUsage(DynamicDrawUsage);
  nodeGeo.setAttribute('position', nodeAttr);
  nodeGeo.setAttribute('color', new BufferAttribute(nodeCol, 3));

  const nodes = new Points(
    nodeGeo,
    new PointsMaterial({
      size: 0.135,
      map: glow,
      vertexColors: true,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      blending: AdditiveBlending,
      sizeAttenuation: true,
    }),
  );

  /* ------------------------------------------------------------- pulses */

  const pulsePos = new Float32Array(PULSE_COUNT * 3);
  const pulseCol = new Float32Array(PULSE_COUNT * 3);
  const pulseT = new Float32Array(PULSE_COUNT);
  const pulseSpeed = new Float32Array(PULSE_COUNT);

  const hot = new Color(COLORS.signalHot);
  const warmCore = new Color(COLORS.signalCore);

  for (let i = 0; i < PULSE_COUNT; i += 1) {
    const i3 = i * 3;
    pulseT[i] = Math.random() * Math.PI * 2;
    pulseSpeed[i] = 0.22 + Math.random() * 0.55;

    mix.copy(hot).lerp(warmCore, Math.random() ** 2.2);
    pulseCol[i3] = mix.r;
    pulseCol[i3 + 1] = mix.g;
    pulseCol[i3 + 2] = mix.b;
  }

  const pulseGeo = new BufferGeometry();
  const pulseAttr = new BufferAttribute(pulsePos, 3);
  pulseAttr.setUsage(DynamicDrawUsage);
  pulseGeo.setAttribute('position', pulseAttr);
  pulseGeo.setAttribute('color', new BufferAttribute(pulseCol, 3));

  const pulses = new Points(
    pulseGeo,
    new PointsMaterial({
      size: 0.3,
      map: glow,
      vertexColors: true,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: AdditiveBlending,
      sizeAttenuation: true,
    }),
  );

  /* ------------------------------------------------------------ streams */

  // Short streaks that travel outward and wrap: finished work leaving.
  const streamPos = new Float32Array(STREAM_COUNT * 6);
  const streamDir = new Float32Array(STREAM_COUNT * 3);
  const streamProgress = new Float32Array(STREAM_COUNT);
  const streamSpeed = new Float32Array(STREAM_COUNT);

  for (let i = 0; i < STREAM_COUNT; i += 1) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    streamDir[i * 3] = Math.sin(phi) * Math.cos(theta);
    streamDir[i * 3 + 1] = Math.sin(phi) * Math.sin(theta);
    streamDir[i * 3 + 2] = Math.cos(phi);
    streamProgress[i] = Math.random();
    streamSpeed[i] = 0.1 + Math.random() * 0.22;
  }

  const streamGeo = new BufferGeometry();
  const streamAttr = new BufferAttribute(streamPos, 3);
  streamAttr.setUsage(DynamicDrawUsage);
  streamGeo.setAttribute('position', streamAttr);

  const streams = new LineSegments(
    streamGeo,
    new LineBasicMaterial({
      color: new Color(COLORS.stream),
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: AdditiveBlending,
    }),
  );

  /* --------------------------------------------------------- core, halo */

  const core = new LineSegments(
    new WireframeGeometry(new IcosahedronGeometry(1.45, 0)),
    new LineBasicMaterial({
      color: new Color(COLORS.core),
      transparent: true,
      opacity: 0.1,
      depthWrite: false,
      blending: AdditiveBlending,
    }),
  );

  const haloGeo = new BufferGeometry();
  haloGeo.setAttribute('position', new BufferAttribute(new Float32Array([0, 0, 0]), 3));

  const halo = new Points(
    haloGeo,
    new PointsMaterial({
      size: 16,
      map: glow,
      color: new Color(COLORS.halo),
      transparent: true,
      opacity: 0.05,
      depthWrite: false,
      blending: AdditiveBlending,
      sizeAttenuation: true,
    }),
  );

  const swarm = new Group();
  swarm.add(halo, rings, shards, nodes, pulses, streams, core);
  scene.add(swarm);

  /* ---------------------------------------------------- the animated field */

  const field = {
    // cursor position on the z=0 plane, in world units, damped by GSAP
    cursorX: 0,
    cursorY: 0,
    cursorPower: 0, // 0 when the pointer is away, 1 when it is over the page
    tiltX: 0,
    tiltY: 0,
    pullX: 0,
    pullY: 0,
    warp: 0,
    scroll: 0,
    order: 1,
    signal: 0.45,
    burst: 0,
    flash: 0,
  };

  /* ------------------------------------------------------- pointer damping */

  if (!REDUCED_MOTION) {
    window.addEventListener(
      'pointermove',
      (event) => {
        const nx = (event.clientX / window.innerWidth) * 2 - 1;
        const ny = (event.clientY / window.innerHeight) * 2 - 1;
        const reach = Math.min(Math.hypot(nx, ny), 1);

        // Where the cursor lands on the z=0 plane, in world units. The dots
        // are pushed away from this point.
        const halfH = Math.tan((camera.fov * Math.PI) / 360) * camera.position.z;
        const halfW = halfH * camera.aspect;

        // The push point gets its own short tween. Sharing the 1.6s one below
        // left it only 58% of the way to the pointer after 400ms, so the dots
        // parted somewhere behind the cursor. The dots already ease into and
        // out of the push themselves; the point they avoid should not lag too.
        gsap.to(field, {
          cursorX: nx * halfW,
          cursorY: -ny * halfH,
          cursorPower: 1,
          duration: 0.35,
          ease: 'power2.out',
          overwrite: 'auto',
        });

        gsap.to(field, {
          tiltX: ny * 0.4,
          tiltY: nx * 0.58,
          pullX: nx * 1.25,
          pullY: -ny * 0.9,
          warp: reach * 0.5,
          duration: 1.6,
          ease: 'power2.out',
          overwrite: 'auto',
        });
      },
      { passive: true },
    );

    window.addEventListener('pointerout', (event) => {
      if (event.relatedTarget) return;
      gsap.to(field, {
        cursorPower: 0,
        tiltX: 0,
        tiltY: 0,
        pullX: 0,
        pullY: 0,
        warp: 0,
        duration: 2.6,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });
  }

  /* ---------------------------------------------------------------- resize */

  // Synchronous on purpose: a resize deferred into requestAnimationFrame is
  // silently dropped while RAF is suspended, leaving a stretched canvas.
  window.addEventListener('resize', () => {
    applySize();
    layoutDots(); // the visible extent changed, so the field must refill it
    dotGeo.attributes.color.needsUpdate = true;
  });

  /* ------------------------------------------------------------------ loop */

  const point = [0, 0, 0];
  let frameId = 0;
  let lastFrame = 0;
  let lastSim = 0;
  let simInterval = 1000 / QUALITY_TIERS[quality].simHz;
  let dotRatio = QUALITY_TIERS[quality].dotRatio;
  let frameAvg = 16.7;
  let slowFrames = 0;
  let fastFrames = 0;

  let onScreen = true;
  let tabVisible = !document.hidden;
  let focused = document.hasFocus();

  const BLURRED_INTERVAL = 1000 / 30;

  /**
   * The expensive half: ~2,000 particle positions rewritten into their
   * buffers. Throttled to the current tier's simulation rate.
   */
  function simulateParticles(time) {
    const { order, burst, warp, scroll } = field;

    const breathe = Math.sin(time * 0.42) * 0.22 + warp * 0.5 + scroll * 0.45;
    const flow = time * 0.045;
    const expand = 1 + burst * 0.55;

    /* nodes — blended between the scattered cloud and the knot */
    for (let i = 0; i < NODE_COUNT; i += 1) {
      const i3 = i * 3;
      const t = tBase[i] + flow;

      knotPoint(t, breathe + Math.sin(t * 1.7 + time * 0.6) * 0.14, point);

      const swell = 1 + Math.sin(time * 0.7 + phases[i]) * (0.16 + warp * 0.4);

      const kx = point[0] * KNOT_SCALE + offsets[i3] * swell;
      const ky = point[1] * KNOT_SCALE + offsets[i3 + 1] * swell;
      const kz = point[2] * KNOT_SCALE + offsets[i3 + 2] * swell;

      const drift = Math.sin(time * 0.25 + phases[i]) * 1.1 * (1 - order);

      nodePos[i3] = (scatter[i3] + drift + (kx - scatter[i3] - drift) * order) * expand;
      nodePos[i3 + 1] =
        (scatter[i3 + 1] - drift + (ky - scatter[i3 + 1] + drift) * order) * expand;
      nodePos[i3 + 2] = (scatter[i3 + 2] + (kz - scatter[i3 + 2]) * order) * expand;
    }
    nodeAttr.needsUpdate = true;

    /* dots — drift, plus a smooth push away from the cursor

       Each dot eases toward its target displacement rather than jumping to
       it, so the field opens as the pointer arrives and closes behind it.
       Dots beyond the radius ease back to zero by the same rule, which is
       what stops the edge of the effect from being a visible ring. */
    const cx = field.cursorX;
    const cy = field.cursorY;
    const power = field.cursorPower;
    const active = Math.ceil(DOT_COUNT * dotRatio);

    for (let i = 0; i < active; i += 1) {
      const i3 = i * 3;

      const bx = dotBase[i3];
      const by = dotBase[i3 + 1];

      let targetX = 0;
      let targetY = 0;

      if (power > 0.001) {
        const dx = bx - cx;
        const dy = by - cy;
        const dist = Math.hypot(dx, dy);

        if (dist < REPEL_RADIUS) {
          // smoothstep rather than a plain square: zero slope at both
          // ends, so dots neither jerk as the cursor reaches them nor stop
          // abruptly at the rim of the effect
          const t = 1 - dist / REPEL_RADIUS;
          const falloff = t * t * (3 - 2 * t);
          const push = (falloff * REPEL_PUSH * power) / (dist || 0.001);
          targetX = dx * push;
          targetY = dy * push;
        }
      }

      dotOffset[i3] += (targetX - dotOffset[i3]) * REPEL_EASE;
      dotOffset[i3 + 1] += (targetY - dotOffset[i3 + 1]) * REPEL_EASE;

      const bob = Math.sin(time * 0.5 + dotPhase[i]) * 0.35;

      dotPos[i3] = bx + dotOffset[i3];
      dotPos[i3 + 1] = by + dotOffset[i3 + 1] + bob;
      dotPos[i3 + 2] = dotBase[i3 + 2] + Math.cos(time * 0.4 + dotPhase[i]) * 0.5;
    }

    // Draw only what was simulated. This used to park the rest at the origin
    // on the theory that they'd be invisible there — but additive blending
    // *sums* overlapping points, so at the lowest tier 3,500 of them stacked
    // into one blinding spot in the middle of the screen.
    dotGeo.setDrawRange(0, active);

    dotAttr.needsUpdate = true;

    /* pulses — ride the ideal strand, so they read as signals not matter */
    for (let i = 0; i < PULSE_COUNT; i += 1) {
      const i3 = i * 3;
      knotPoint(pulseT[i] + time * pulseSpeed[i] * 0.16, breathe, point);
      pulsePos[i3] = point[0] * KNOT_SCALE * expand;
      pulsePos[i3 + 1] = point[1] * KNOT_SCALE * expand;
      pulsePos[i3 + 2] = point[2] * KNOT_SCALE * expand;
    }
    pulseAttr.needsUpdate = true;

    /* streams — travel outward from the core, then wrap around */
    for (let i = 0; i < STREAM_COUNT; i += 1) {
      streamProgress[i] += streamSpeed[i] * 0.006;
      if (streamProgress[i] > 1) streamProgress[i] -= 1;

      const d3 = i * 3;
      const s6 = i * 6;
      const travel = 5 + streamProgress[i] * 30;
      const tail = travel + 1.6 + streamProgress[i] * 2.4;

      streamPos[s6] = streamDir[d3] * travel;
      streamPos[s6 + 1] = streamDir[d3 + 1] * travel;
      streamPos[s6 + 2] = streamDir[d3 + 2] * travel;
      streamPos[s6 + 3] = streamDir[d3] * tail;
      streamPos[s6 + 4] = streamDir[d3 + 1] * tail;
      streamPos[s6 + 5] = streamDir[d3 + 2] * tail;
    }
    streamAttr.needsUpdate = true;
  }

  /**
   * The cheap half: a few dozen object transforms and material values. Runs
   * every rendered frame, so rotation stays perfectly smooth even when the
   * particle simulation is stepping at a lower rate.
   */
  function updateObjects(time) {
    const { order, signal, burst, flash } = field;
    const expand = 1 + burst * 0.55;

    // The centrepiece grows as the page scrolls. Smoothstep, so the growth
    // starts gently, builds through the middle of the page and settles at
    // the end rather than tracking the scrollbar linearly.
    const p = Math.min(1, Math.max(0, field.scroll));
    const grown = p * p * (3 - 2 * p);
    swarm.scale.setScalar(1 + grown * SCROLL_GROWTH);

    /* rings */
    rings.children.forEach((ring, i) => {
      const [tx, ty, tz] = ring.userData.baseTilt;
      ring.rotation.x = tx + time * ring.userData.rate;
      ring.rotation.y = ty + time * ring.userData.rate * 0.6;
      ring.rotation.z = tz + Math.sin(time * 0.1 + i) * 0.12;
      ring.scale.setScalar((0.72 + order * 0.28) * expand);
      ring.material.opacity = (0.035 + order * 0.065) * (1 + flash * 1.4);
    });

    /* shards */
    shards.children.forEach((shard, i) => {
      const u = shard.userData;
      const a = u.orbitPhase + time * u.orbitSpeed;
      const r = u.orbitRadius * (0.82 + order * 0.18) * expand;

      shard.position.set(
        Math.cos(a) * r,
        Math.sin(a) * r * 0.55 + Math.sin(time * 0.4 + u.bob) * 1.2,
        Math.sin(a) * r * Math.sin(u.orbitTilt),
      );

      shard.rotation.x += u.spin[0] * 0.012;
      shard.rotation.y += u.spin[1] * 0.012;
      shard.rotation.z += u.spin[2] * 0.012;

      shard.material.opacity = (0.05 + order * 0.1) * (1 + flash * 1.2);
      shard.scale.setScalar(u.scale * (1 + flash * 0.18 + Math.sin(time + i) * 0.04));
    });

    /* core */
    core.rotation.y = time * 0.18;
    core.rotation.x = -time * 0.11;
    core.scale.setScalar((0.8 + signal * 0.3 + flash * 0.35) * expand);
    core.material.opacity = (0.07 + signal * 0.17) * (1 + flash * 0.9);

    /* intensities follow the story */
    // As the spiral grows, the same 1,500 particles are spread over a much
    // larger shape and it starts to dissolve. Growing the particles with it
    // keeps it reading as one solid form; a slight fade keeps a spiral that
    // fills the screen behind the text rather than competing with it.
    nodes.material.size = 0.135 * (1 + grown * 0.75);
    nodes.material.opacity = (0.28 + order * 0.3 + flash * 0.12) * (1 - grown * 0.18);
    dots.material.opacity = 0.5 + flash * 0.18;
    pulses.material.opacity = signal * 0.95;
    pulses.material.size = (0.22 + signal * 0.16 + flash * 0.08) * (1 + grown * 0.6);
    streams.material.opacity = signal * 0.3 + flash * 0.12;
    halo.material.opacity = 0.03 + signal * 0.075 + flash * 0.06;
    halo.material.size = 13 + signal * 7 + flash * 4;
  }

  function applyQuality() {
    const tier = QUALITY_TIERS[quality];
    simInterval = 1000 / tier.simHz;
    farDots.visible = tier.farDots;
    dotRatio = tier.dotRatio; // how much of the interactive field is simulated
    applySize(); // the pixel-ratio cap moved with the tier
  }

  /** Watch the frame budget and move between tiers, with hysteresis. */
  function policeFrameRate(dt) {
    // Ignore the huge dt after a pause, and anything already throttled.
    if (dt > 100 || !focused) return;

    frameAvg += (dt - frameAvg) * 0.05;

    if (frameAvg > SLOW_FRAME_MS && quality > 0) {
      slowFrames += 1;
      fastFrames = 0;
      if (slowFrames > FRAMES_BEFORE_DOWNGRADE) {
        quality -= 1;
        slowFrames = 0;
        applyQuality();
      }
      return;
    }

    if (frameAvg < FAST_FRAME_MS && quality < QUALITY_TIERS.length - 1) {
      fastFrames += 1;
      slowFrames = 0;
      if (fastFrames > FRAMES_BEFORE_UPGRADE) {
        quality += 1;
        fastFrames = 0;
        applyQuality();
      }
      return;
    }

    slowFrames = 0;
    fastFrames = 0;
  }

  function render(now) {
    frameId = requestAnimationFrame(render);

    if (!focused && now - lastFrame < BLURRED_INTERVAL) return;
    const dt = now - lastFrame;
    lastFrame = now;

    const time = now / 1000;

    if (REDUCED_MOTION) {
      simulateParticles(0);
      updateObjects(0);
      renderer.render(scene, camera);
      canvas.classList.add('is-ready');
      stop();
      return;
    }

    policeFrameRate(dt);

    // Simulate at the tier's rate; render at the display's.
    if (now - lastSim >= simInterval) {
      lastSim = now;
      simulateParticles(time);
    }

    updateObjects(time);

    swarm.rotation.y = time * 0.055 + field.tiltY + field.scroll * 0.5;
    swarm.rotation.x = Math.sin(time * 0.13) * 0.1 + field.tiltX;
    swarm.rotation.z = time * 0.012;

    swarm.position.x = field.pullX;
    swarm.position.y = field.pullY;

    // Scroll dollies the camera through the scene, so the layers separate.
    camera.position.z = CAMERA_REST_Z - field.scroll * 7;
    camera.position.y = field.scroll * 3;
    camera.lookAt(0, 0, 0);

    farDots.rotation.y = -time * 0.012;
    farDots.rotation.x = time * 0.005;

    // The interactive field deliberately does not rotate. Any turn slowly
    // swings the corners of its rectangle out of view — the longer you stay,
    // the more bare background shows — and it detaches the repel from the
    // pointer, since the push is computed in the field's own coordinates.

    renderer.render(scene, camera);

    // The canvas is transparent until this first frame exists; fade it in
    // (see #webgl-canvas in style.css) rather than popping the scene in.
    if (!canvas.classList.contains('is-ready')) canvas.classList.add('is-ready');
  }

  function start() {
    if (frameId) return;
    lastFrame = performance.now();
    frameId = requestAnimationFrame(render);
  }

  function stop() {
    if (!frameId) return;
    cancelAnimationFrame(frameId);
    frameId = 0;
  }

  function sync() {
    if (onScreen && tabVisible) {
      applySize();
      start();
    } else {
      stop();
    }
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        onScreen = entries.some((e) => e.isIntersecting);
        sync();
      },
      { threshold: 0 },
    ).observe(canvas);
  }

  document.addEventListener('visibilitychange', () => {
    tabVisible = !document.hidden;
    sync();
  });

  window.addEventListener('blur', () => {
    focused = false;
  });
  window.addEventListener('focus', () => {
    focused = true;
  });

  start();

  if (import.meta.env.DEV) {
    window.__aissist = {
      field,
      swarm,
      renderer,
      camera,
      layers: { farDots, dots, rings, shards, nodes, pulses, streams, core, halo },
      counts: {
        NODE_COUNT,
        FAR_DOT_COUNT,
        DOT_COUNT,
        PULSE_COUNT,
        STREAM_COUNT,
        SHARD_COUNT,
        RING_COUNT: rings.children.length,
      },
      isRunning: () => Boolean(frameId),
      pixelRatio,
      gsap,
      // dev-only: drive the tiers by hand to check each one applies
      forceQuality: (tier) => {
        quality = Math.max(0, Math.min(QUALITY_TIERS.length - 1, tier));
        applyQuality();
        return QUALITY_TIERS[quality];
      },
      dotRatio: () => dotRatio,
      // dev-only: drive one frame by hand, for testing without a live RAF
      step: (t) => {
        const now = t ?? performance.now();
        lastSim = 0; // force the simulation to run on this step
        simulateParticles(now / 1000);
        updateObjects(now / 1000);
        renderer.render(scene, camera);
      },
      quality: () => ({
        tier: quality,
        ...QUALITY_TIERS[quality],
        avgFrameMs: +frameAvg.toFixed(2),
        fps: +(1000 / frameAvg).toFixed(1),
        simHz: 1000 / simInterval,
      }),
    };
  }

  return { field };
}

/* ==========================================================================
   Motion
   ========================================================================== */

function flashField(field) {
  if (!field || REDUCED_MOTION) return;
  gsap
    .timeline()
    .to(field, { flash: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' })
    .to(field, { flash: 0, duration: 1.5, ease: EASE.inOut });
}

/**
 * Headings rise word by word from behind a clipping box around each line, so a
 * word is only ever visible inside its own line. Only transform is animated,
 * which stays on the compositor with the 3D scene running.
 *
 * This replaced a split into loose inline-block words with nothing clipping
 * them: each word travelled up from a full line below, straight through the
 * line underneath and the paragraph under the headline, so for half a second
 * the hero was a pile of overlapping letters.
 *
 * The heading is hidden now (matching .reveal-pending, so the first paint and
 * this agree) and split only when it plays: by then the web fonts are in, so
 * the line breaks are the real ones. It is reverted when done, which hands
 * back plain text — crisp, with no per-word layers, and free to reflow on
 * resize. A stalled frame loop is covered by initRevealWatchdog.
 */
function buildHeadingReveal(el, field) {
  gsap.set(el, { opacity: 0 });
  el.dataset.headingReveal = '';

  const run = () => {
    const split = SplitText.create(el, {
      type: 'lines,words',
      mask: 'lines',
      linesClass: 'heading-line',
      wordsClass: 'heading-word',
    });
    // Far enough down to clear the mask's extra room for descenders (0.16em).
    gsap.set(split.words, { yPercent: 125 });
    gsap.set(el, { opacity: 1 });
    if (field) flashField(field);
    gsap.to(split.words, {
      yPercent: 0,
      duration: 1.1,
      ease: EASE.settle,
      stagger: 0.05,
      onComplete: () => split.revert(),
    });
  };

  const start = () => {
    if (!document.fonts || document.fonts.status === 'loaded') return run();
    // Never hold a headline hostage to a slow font server.
    Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 600))]).then(
      run,
    );
  };

  // A paused timeline, so callers can still delay() and play() it like any
  // other reveal. The empty tween gives it a length for the call to sit in.
  const tl = gsap.timeline({ paused: true });
  tl.call(start, [], 0).to({}, { duration: 0.01 });

  return tl;
}

/**
 * Every scroll reveal uses fromTo with an explicit end state, never from().
 *
 * A from() takes the element's *current* value as its destination. When two
 * from() tweens touch the same element — a generic [data-reveal] plus a
 * section's own entrance — the second one is built after the first has
 * already hidden the element, records "hidden" as its destination, and the
 * element finishes invisible. That is exactly what left the second and third
 * cards of the old "What we build" row blank. With explicit ends, stacking
 * two reveals can no longer leave anything hidden.
 */
function reveal(targets, from, options = {}) {
  const { trigger, start = 'top 88%', stagger = 0, delay = 0, paused = false } = options;
  return gsap.fromTo(
    targets,
    { opacity: 0, ...from },
    {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: 1.1,
      ease: EASE.settle,
      stagger,
      delay,
      paused,
      // Always written now, including the paused above-the-fold ones: the
      // intro plays them with a stagger delay, and until their start was
      // written they sat visible, then vanished, then rose in.
      immediateRender: true,
      scrollTrigger: paused
        ? undefined
        : { trigger: trigger || targets, start, once: true },
    },
  );
}

/**
 * The illustrations have small ambient loops — data moving down a workflow,
 * signals along integration spokes, a typing indicator. They only run while
 * the illustration is on screen, so a dozen loops cost nothing when scrolled
 * past.
 */
function whileVisible(element, timeline) {
  timeline.pause();
  ScrollTrigger.create({
    trigger: element,
    start: 'top bottom',
    end: 'bottom top',
    onToggle: (self) => (self.isActive ? timeline.play() : timeline.pause()),
  });
}

function animateFlowVisual(visual) {
  const wires = visual.querySelectorAll('.wire');
  const packets = visual.querySelectorAll('.packet');
  const inputs = visual.querySelectorAll('.flow__col:not(.flow__col--out) .node');
  const outputs = visual.querySelectorAll('.flow__col--out .node');
  const core = visual.querySelector('.flow__core');

  // entrance: wires draw in, then the nodes arrive
  wires.forEach((wire) => {
    const length = wire.getTotalLength();
    gsap.set(wire, { strokeDasharray: length, strokeDashoffset: length });
  });

  gsap
    .timeline({ scrollTrigger: { trigger: visual, start: 'top 80%', once: true } })
    .fromTo(
      inputs,
      { opacity: 0, x: -14 },
      { opacity: 1, x: 0, duration: 0.7, ease: EASE.settle, stagger: 0.08 },
    )
    .to(
      [...wires].slice(0, 3),
      { strokeDashoffset: 0, duration: 0.8, ease: EASE.inOut, stagger: 0.06 },
      0.2,
    )
    .fromTo(
      core,
      { opacity: 0, scale: 0.6 },
      { opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(1.6)' },
      0.55,
    )
    .to(
      [...wires].slice(3),
      { strokeDashoffset: 0, duration: 0.8, ease: EASE.inOut, stagger: 0.06 },
      0.85,
    )
    .fromTo(
      outputs,
      { opacity: 0, x: 14 },
      { opacity: 1, x: 0, duration: 0.7, ease: EASE.settle, stagger: 0.1 },
      1.2,
    );

  // ambient: packets ride input wire -> core -> output wire, staggered
  const loop = gsap.timeline({ repeat: -1 });
  packets.forEach((packet, i) => {
    const inWire = wires[i];
    const outWire = wires[3 + i];
    const inLen = inWire.getTotalLength();
    const outLen = outWire.getTotalLength();
    const progress = { t: 0 };

    loop.fromTo(
      progress,
      { t: 0 },
      {
        t: 1,
        duration: 2.4,
        ease: 'power1.inOut',
        onUpdate: () => {
          const t = progress.t;
          const point =
            t < 0.5
              ? inWire.getPointAtLength(inLen * (t / 0.5))
              : outWire.getPointAtLength(outLen * ((t - 0.5) / 0.5));
          packet.setAttribute('cx', point.x);
          packet.setAttribute('cy', point.y);
          packet.style.opacity = t < 0.04 || t > 0.96 ? '0' : '1';
        },
      },
      i * 0.8,
    );
  });
  // pulse the inner label, not .flow__core itself: the entrance animates the
  // core's scale, and two tweens writing one property jitter
  loop.to(
    core.querySelector('b'),
    { scale: 1.12, duration: 0.4, yoyo: true, repeat: 5, ease: 'sine.inOut' },
    0.9,
  );

  whileVisible(visual, loop);
}

function animateAskVisual(visual) {
  const q = visual.querySelector('.bubble--q');
  const a = visual.querySelector('.bubble--a');
  const source = visual.querySelector('.source');
  const typing = visual.querySelector('.typing');

  gsap
    .timeline({ scrollTrigger: { trigger: visual, start: 'top 78%', once: true } })
    .fromTo(
      q,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.6, ease: EASE.settle },
    )
    .fromTo(typing, { opacity: 0 }, { opacity: 1, duration: 0.25 }, 0.5)
    .to(typing, { opacity: 0, duration: 0.2 }, 1.5)
    .fromTo(
      a,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.7, ease: EASE.settle },
      1.55,
    )
    .fromTo(
      source,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2)' },
      2.05,
    );

  animateTyping(visual, typing);
}

function animateHubVisual(visual) {
  const spokes = visual.querySelectorAll('.spoke');
  const apps = visual.querySelectorAll('.hub__app');
  const core = visual.querySelector('.hub__core');

  spokes.forEach((spoke) => {
    const length = Math.hypot(
      spoke.x2.baseVal.value - spoke.x1.baseVal.value,
      spoke.y2.baseVal.value - spoke.y1.baseVal.value,
    );
    spoke.dataset.length = String(length);
    gsap.set(spoke, { strokeDasharray: length, strokeDashoffset: length });
  });

  gsap
    .timeline({ scrollTrigger: { trigger: visual, start: 'top 80%', once: true } })
    .fromTo(
      core,
      { opacity: 0, scale: 0.5 },
      { opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(1.7)' },
    )
    .to(
      spokes,
      { strokeDashoffset: 0, duration: 0.7, ease: EASE.inOut, stagger: 0.06 },
      0.3,
    )
    .fromTo(
      apps,
      // xPercent/yPercent keep the CSS centring: once GSAP owns the transform it
      // would otherwise replace translate(-50%, -50%) and knock them off-centre
      // x/y pinned to 0 as well: GSAP parses the stylesheet's
      // translate(-50%, -50%) into pixel x/y, and left alone those pixels
      // stacked on top of the percentages and shifted every label twice.
      { opacity: 0, scale: 0.8, x: 0, y: 0, xPercent: -50, yPercent: -50 },
      {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        xPercent: -50,
        yPercent: -50,
        duration: 0.6,
        ease: 'back.out(1.8)',
        stagger: 0.07,
      },
      0.6,
    );

  // ambient: a short lit dash runs out along each spoke in turn. It rides a
  // copy of the spoke laid on top, so the spoke itself never flickers.
  const svg = visual.querySelector('.hub__spokes');
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4 });
  spokes.forEach((spoke, i) => {
    const length = Number(spoke.dataset.length);
    const signal = spoke.cloneNode();
    signal.setAttribute('class', 'spoke spoke--signal');
    svg.appendChild(signal);
    gsap.set(signal, { strokeDasharray: `12 ${length}`, strokeDashoffset: 12 });

    loop.to(
      signal,
      { strokeDashoffset: -length, duration: 1.1, ease: 'power1.inOut' },
      i * 0.35,
    );
    loop.set(signal, { strokeDashoffset: 12 }, i * 0.35 + 1.1);
    loop.fromTo(
      apps[i],
      { '--glow': 0 },
      { '--glow': 1, duration: 0.3, yoyo: true, repeat: 1 },
      i * 0.35 + 0.9,
    );
  });
  // same as the flow core: pulse the inner mark, not the entrance-scaled wrapper
  loop.to(
    core.querySelector('svg'),
    { scale: 1.1, duration: 0.5, yoyo: true, repeat: 3, ease: 'sine.inOut' },
    0,
  );

  whileVisible(visual, loop);
}

function animateTyping(visual, typing) {
  if (!typing) return;
  const dots = typing.querySelectorAll('span');
  const loop = gsap.timeline({ repeat: -1 });
  loop.to(dots, { y: -3, opacity: 1, duration: 0.3, ease: 'sine.out', stagger: 0.12 });
  loop.to(
    dots,
    { y: 0, opacity: 0.4, duration: 0.3, ease: 'sine.in', stagger: 0.12 },
    0.3,
  );
  whileVisible(visual, loop);
}

function animateChatVisual(visual) {
  const messages = visual.querySelectorAll('.msg');
  const typing = visual.querySelector('.typing');

  const tl = gsap.timeline({
    scrollTrigger: { trigger: visual, start: 'top 75%', once: true },
  });
  messages.forEach((msg, i) => {
    const mine = msg.classList.contains('msg--you');
    tl.fromTo(
      msg,
      { opacity: 0, y: 16, x: mine ? 16 : -16 },
      { opacity: 1, y: 0, x: 0, duration: 0.6, ease: EASE.settle },
      i * 0.55,
    );
  });
  tl.fromTo(
    typing,
    { opacity: 0 },
    { opacity: 1, duration: 0.3 },
    messages.length * 0.55,
  );

  animateTyping(visual, typing);
}

/**
 * Paragraphs rise into place one line at a time, each line from behind its own
 * mask. The split happens at the moment the paragraph is about to animate —
 * not at load — so it uses the real, final line breaks (fonts loaded, window
 * at its current width), and it is reverted as soon as the animation ends, so
 * a later resize reflows ordinary text rather than a frozen stack of lines.
 */
function lineReveal(el, { start = 'top 88%' } = {}) {
  gsap.set(el, { opacity: 0 });

  ScrollTrigger.create({
    trigger: el,
    start,
    once: true,
    onEnter: () => {
      const split = SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'split-line',
      });
      gsap.set(el, { opacity: 1 });
      gsap.fromTo(
        split.lines,
        { yPercent: 105, opacity: 0 },
        {
          yPercent: 0,
          opacity: 1,
          duration: 1.05,
          ease: EASE.settle,
          stagger: 0.09,
          onComplete: () => split.revert(),
        },
      );
    },
  });
}

/**
 * The pictures tilt toward the pointer in 3D, with a soft glare that follows
 * it. Only on devices with a real hover pointer: on touch there is no hover,
 * and a tilt that fires on tap reads as a glitch.
 */
function initTilt() {
  if (REDUCED_MOTION || !window.matchMedia('(hover: hover) and (pointer: fine)').matches)
    return;

  document.querySelectorAll('.visual').forEach((card) => {
    gsap.set(card, { transformPerspective: 1000 });
    const tiltX = gsap.quickTo(card, 'rotationX', { duration: 0.7, ease: 'power3.out' });
    const tiltY = gsap.quickTo(card, 'rotationY', { duration: 0.7, ease: 'power3.out' });

    card.addEventListener('pointermove', (event) => {
      const r = card.getBoundingClientRect();
      const px = (event.clientX - r.left) / r.width - 0.5;
      const py = (event.clientY - r.top) / r.height - 0.5;
      tiltX(-py * 7);
      tiltY(px * 9);
      card.style.setProperty('--mx', `${(px + 0.5) * 100}%`);
      card.style.setProperty('--my', `${(py + 0.5) * 100}%`);
    });

    card.addEventListener('pointerleave', () => {
      tiltX(0);
      tiltY(0);
    });
  });
}

/** The large buttons lean toward the pointer, then settle back when it leaves. */
function initMagnetic() {
  if (REDUCED_MOTION || !window.matchMedia('(hover: hover) and (pointer: fine)').matches)
    return;

  document.querySelectorAll('.cta--lg').forEach((button) => {
    const moveX = gsap.quickTo(button, 'x', { duration: 0.5, ease: 'power3.out' });
    const moveY = gsap.quickTo(button, 'y', { duration: 0.5, ease: 'power3.out' });

    button.addEventListener('pointermove', (event) => {
      const r = button.getBoundingClientRect();
      moveX((event.clientX - (r.left + r.width / 2)) * 0.22);
      moveY((event.clientY - (r.top + r.height / 2)) * 0.32);
    });

    button.addEventListener('pointerleave', () => {
      moveX(0);
      moveY(0);
    });
  });
}

/** Wire the social icons from SOCIAL_LINKS; leave empty ones inert. */
function initSocialLinks() {
  document.querySelectorAll('[data-social]').forEach((link) => {
    const url = (SOCIAL_LINKS[link.dataset.social] || '').trim();

    if (url) {
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.removeAttribute('aria-disabled');
      link.classList.remove('social__link--pending');
      return;
    }

    link.removeAttribute('href'); // no href: not a link, no jump to the top
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('title', `${link.getAttribute('aria-label')} — coming soon`);
    link.classList.add('social__link--pending');
  });
}

function initMotion(field) {
  if (REDUCED_MOTION) {
    gsap.set('[data-reveal], [data-split]', { opacity: 1, y: 0 });
    return { playIntro: () => {} };
  }

  const intro = [];

  /* --- headings --------------------------------------------------------- */

  // The headline pulses the scene on the first visit, when it is the payoff
  // of the logo intro. Arriving from another page, the scene is still fading
  // in and a bright pulse on top of that reads as a flash.
  const hero = document.querySelector('[data-split]');
  const arriving = document.documentElement.classList.contains('boot-mini');
  if (hero) intro.push(buildHeadingReveal(hero, arriving ? null : field));

  gsap.utils
    .toArray('.section-head__title, .slide__title, .feature__title, .band__title')
    .forEach((el) => {
      const tl = buildHeadingReveal(el, field);
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: () => tl.play(),
      });
    });

  /* --- generic reveals -------------------------------------------------- */

  const aboveFold = (el) => Boolean(el.closest('.hero, .page-head'));

  gsap.utils.toArray('[data-reveal]').forEach((el) => {
    const paused = aboveFold(el);
    const tween = reveal(el, { y: 20 }, { paused });
    if (paused) intro.push(tween);
  });

  /* --- paragraphs, line by line ----------------------------------------- */

  gsap.utils.toArray('[data-lines]').forEach((el) => lineReveal(el));

  /* --- section labels: the red rule draws in beside the text ------------ */

  gsap.utils.toArray('.section-head__idx').forEach((label) => {
    gsap.fromTo(
      label,
      { '--line': 0 },
      {
        '--line': 1,
        duration: 1,
        ease: EASE.inOut,
        scrollTrigger: { trigger: label, start: 'top 88%', once: true },
      },
    );
  });

  /* --- hero path: the three steps arrive in order, linked as they go ---- */

  const pathSteps = gsap.utils.toArray('.hero__path li');
  if (pathSteps.length) {
    intro.push(
      gsap.fromTo(
        pathSteps,
        { opacity: 0, x: -12, '--link': 0 },
        {
          opacity: 1,
          x: 0,
          '--link': 1,
          duration: 0.8,
          ease: EASE.settle,
          stagger: 0.16,
          delay: 0.5,
          paused: true,
          immediateRender: true,
        },
      ),
    );
  }

  /* --- services: each slide's text and picture arrive from opposite sides */

  gsap.utils.toArray('.slide').forEach((slide) => {
    const flip = slide.classList.contains('slide--flip');
    const number = slide.querySelector('.slide__num');
    const chips = slide.querySelectorAll('.chips li');
    const visual = slide.querySelector('.visual');

    reveal(number, { y: 14 }, { trigger: slide, start: 'top 78%' });
    // chips pop in one after another once the paragraph has begun
    reveal(
      chips,
      { y: 10, scale: 0.85 },
      { trigger: slide, start: 'top 70%', stagger: 0.08, delay: 0.35 },
    );
    reveal(
      visual,
      { x: flip ? -50 : 50, scale: 0.97 },
      { trigger: slide, start: 'top 78%' },
    );

    // a gentle parallax on the picture while the slide scrolls through
    gsap.fromTo(
      visual,
      { yPercent: 6 },
      {
        yPercent: -6,
        ease: 'none',
        scrollTrigger: {
          trigger: slide,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      },
    );
  });

  document.querySelectorAll('.visual--flow').forEach(animateFlowVisual);
  document.querySelectorAll('.visual--ask').forEach(animateAskVisual);
  document.querySelectorAll('.visual--hub').forEach(animateHubVisual);
  document.querySelectorAll('.visual--chat').forEach(animateChatVisual);

  /* --- "in every conversation": the promises arrive one by one --------- */

  const points = gsap.utils.toArray('.feature__points li');
  if (points.length) {
    reveal(
      points,
      { x: -18 },
      { trigger: '.feature__points', start: 'top 86%', stagger: 0.12 },
    );
  }

  /* --- closing band ---------------------------------------------------- */

  const band = document.querySelector('.band');
  if (band) {
    reveal(
      band.querySelector('.cta'),
      { y: 18, scale: 0.96 },
      { trigger: band, start: 'top 72%' },
    );
    reveal(
      band.querySelector('.band__email'),
      { y: 12 },
      { trigger: band, start: 'top 68%', delay: 0.2 },
    );
  }

  /* --- footer ---------------------------------------------------------- */

  const footer = document.querySelector('.footer');
  if (footer) {
    reveal(
      footer.querySelectorAll(
        '.footer__brand, .footer__nav a, .footer__email, .footer__legal',
      ),
      { y: 12 },
      {
        trigger: footer,
        start: 'top 96%',
        stagger: 0.05,
      },
    );
    reveal(
      footer.querySelectorAll('.social li'),
      { y: 10, scale: 0.7 },
      {
        trigger: footer,
        start: 'top 96%',
        stagger: 0.08,
        delay: 0.25,
      },
    );
  }

  /* --- nav density ------------------------------------------------------ */

  ScrollTrigger.create({
    start: 'top -70',
    end: 99999,
    onToggle: (self) =>
      document.querySelector('.nav')?.classList.toggle('nav--scrolled', self.isActive),
  });

  /* --- page scroll feeds the scene -------------------------------------- */

  if (field) {
    ScrollTrigger.create({
      start: 'top top',
      end: 'max',
      onUpdate: (self) => {
        // Lenis already smooths the input; a short tween keeps the 3D from
        // tracking every sub-pixel.
        gsap.to(field, {
          scroll: self.progress,
          duration: 0.5,
          ease: 'power2.out',
          overwrite: 'auto',
        });
      },
    });
  }

  return {
    playIntro: () => intro.forEach((tl, i) => tl.delay(i * 0.06).play()),
  };
}

/**
 * Below-the-fold reveals pre-hide their targets, which is correct while the
 * frame loop is running. If it never starts — a tab opened in the background,
 * a script error upstream, a browser without requestAnimationFrame — that
 * hidden state would be permanent and the page would read as empty. This
 * notices and undoes it.
 */
function initRevealWatchdog() {
  if (REDUCED_MOTION) return;

  setTimeout(() => {
    if (gsap.ticker.frame > 2) return; // the loop is running; nothing to do
    // Only the properties the reveals animate — never clearProps: 'all', which
    // empties the whole style attribute and takes author values with it.
    gsap.set(
      '[data-reveal], [data-lines], [data-heading-reveal], .split-line, .heading-word, ' +
        '.slide__num, .chips li, .visual, ' +
        '.node, .flow__core, .bubble, .source, .hub__core, .msg, .feature__points li, ' +
        '.band .cta, .band__email, .footer__brand, .footer__nav a, .footer__email, ' +
        '.footer__legal, .social li, .hero__path li',
      { clearProps: 'opacity,transform,translate,rotate,scale' },
    );
    // the labels keep their centring, which lives in the transform
    gsap.set('.hub__app', {
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0,
      xPercent: -50,
      yPercent: -50,
    });
    gsap.set('.section-head__idx', { '--line': 1 });
    gsap.set('.hero__path li', { '--link': 1 });
    gsap.set('.wire, .spoke:not(.spoke--signal)', {
      strokeDasharray: 'none',
      strokeDashoffset: 0,
    });
  }, 2500);
}

/* ==========================================================================
   Contact form (Netlify)
   ========================================================================== */

function initForm() {
  const form = document.querySelector('form[name="contact-aissist"]');
  if (!form) return;

  const status = document.querySelector('[data-form-status]');

  // The required attributes mean the browser blocks an incomplete form before
  // this fires. Never preventDefault a valid submit — Netlify needs the POST.
  form.addEventListener('submit', (event) => {
    if (status) {
      status.textContent = 'Sending…';
      status.style.color = 'var(--silver-300)';
    }

    if (import.meta.env.DEV) {
      // No Netlify backend on the dev server; this branch is stripped in prod.
      event.preventDefault();
      if (status) status.textContent = 'Valid — Netlify will receive this once deployed.';
      form.reset();
    }
  });
}

/* ============================================================== boot === */

async function boot() {
  const lenis = initSmoothScroll();

  const canvas = document.getElementById('webgl-canvas');
  const scene = canvas ? initScene(canvas) : null;
  const field = scene?.field;

  initSocialLinks();
  const { playIntro } = initMotion(field);
  // Every reveal has now written its own hidden start, so the CSS hold that
  // kept the first paint hidden can go without anything showing in between.
  document.documentElement.classList.remove('reveal-pending');
  initTilt();
  initMagnetic();
  initForm();
  initRevealWatchdog();
  initPageTransitions(lenis, field);

  if (field) gsap.set(field, { order: 1, signal: 0.42 });

  const year = document.querySelector('[data-year]');
  if (year) year.textContent = String(new Date().getFullYear());

  // Late fonts change every metric ScrollTrigger measured.
  document.fonts?.ready.then(() => ScrollTrigger.refresh());

  if (import.meta.env.DEV && window.__aissist) window.__aissist.lenis = lenis;

  // Hold the above-the-fold reveals until the loader has cleared, so the
  // first thing a visitor sees is one continuous move, not two.
  await runLoader(lenis);
  playIntro();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
